import React, { Component } from 'react';
import PropTypes from 'prop-types';
import chevronSvg from '../../../../../assets/images/chevron-icon.svg';
import searchGrey from '../../../../../assets/images/search-grey.svg';
import closeGrey from '../../../../../assets/images/close-small-grey.svg';

class FairDropdown extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isOpen: false,
            searchTerm: '',
        };
        this.dropdownRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event) => {
        if (
            this.dropdownRef.current &&
            !this.dropdownRef.current.contains(event.target)
        ) {
            this.setState({ isOpen: false, searchTerm: '' });
        }
    };

    toggleOpen = () => {
        this.setState((prevState) => ({
            isOpen: !prevState.isOpen,
            searchTerm: prevState.isOpen ? '' : prevState.searchTerm,
        }));
    };

    handleSearchChange = (event) => {
        this.setState({ searchTerm: event.target.value });
    };

    getFilteredOptions = () => {
        const { options } = this.props;
        const { searchTerm } = this.state;
        const normalizedSearch = searchTerm.trim().toLowerCase();

        if (!normalizedSearch) {
            return options;
        }

        return options.filter((option) =>
            String(option.label).toLowerCase().includes(normalizedSearch)
        );
    };

    isOptionSelected = (value) => {
        const { multi, value: selectedValue } = this.props;

        if (multi) {
            return (selectedValue || []).map(String).includes(String(value));
        }

        return String(selectedValue) === String(value);
    };

    hasClearableValue = () => {
        const { clearable, multi, value, clearValue } = this.props;

        if (!clearable) {
            return false;
        }

        if (multi) {
            return (value || []).length > 0;
        }

        if (value === undefined || value === null || value === '') {
            return false;
        }

        if (clearValue !== undefined) {
            return String(value) !== String(clearValue);
        }

        return true;
    };

    handleClear = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const { multi, onChange, clearValue } = this.props;

        if (multi) {
            onChange([]);
        } else {
            onChange(clearValue !== undefined ? clearValue : '');
        }

        this.setState({ isOpen: false, searchTerm: '' });
    };

    handleOptionClick = (option) => {
        const { multi, value, onChange } = this.props;

        if (multi) {
            const current = (value || []).map(String);
            const optionValue = String(option.value);
            const isSelected = current.includes(optionValue);
            const nextValue = isSelected
                ? current.filter((item) => item !== optionValue)
                : [...current, optionValue];

            onChange(nextValue);
            return;
        }

        onChange(option.value);
        this.setState({ isOpen: false, searchTerm: '' });
    };

    getDisplayLabel = () => {
        const { multi, value, options, placeholder, allSelectedLabel } =
            this.props;

        if (multi) {
            const selected = value || [];

            if (!selected.length) {
                return placeholder;
            }

            if (selected.length === options.length && allSelectedLabel) {
                return allSelectedLabel;
            }

            if (selected.length === 1) {
                const match = options.find(
                    (option) => String(option.value) === String(selected[0])
                );
                return match ? match.label : placeholder;
            }

            const firstMatch = options.find(
                (option) => String(option.value) === String(selected[0])
            );
            return firstMatch
                ? `${firstMatch.label} +${selected.length - 1}`
                : placeholder;
        }

        const match = options.find(
            (option) => String(option.value) === String(value)
        );
        return match ? match.label : placeholder;
    };

    render() {
        const { multi, searchable, className } = this.props;
        const { isOpen, searchTerm } = this.state;
        const filteredOptions = this.getFilteredOptions();
        const showClear = this.hasClearableValue();

        return (
            <div
                className={`fair-dropdown ${className || ''} ${
                    isOpen ? 'fair-dropdown--open' : ''
                }`}
                ref={this.dropdownRef}
            >
                <button
                    type="button"
                    className="fair-dropdown__trigger"
                    onClick={this.toggleOpen}
                >
                    <span className="fair-dropdown__label">
                        {this.getDisplayLabel()}
                    </span>
                    <span className="fair-dropdown__actions">
                        {showClear && (
                            <span
                                role="button"
                                tabIndex={0}
                                className="fair-dropdown__clear"
                                onClick={this.handleClear}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        this.handleClear(event);
                                    }
                                }}
                            >
                                <img src={closeGrey} alt="" />
                            </span>
                        )}
                        <img
                            src={chevronSvg}
                            alt=""
                            className="fair-dropdown__chevron"
                        />
                    </span>
                </button>

                {isOpen && (
                    <div className="fair-dropdown__menu">
                        {searchable && (
                            <div className="fair-dropdown__search">
                                <img
                                    src={searchGrey}
                                    alt=""
                                    className="fair-dropdown__search-icon"
                                />
                                <input
                                    type="text"
                                    className="fair-dropdown__search-input"
                                    value={searchTerm}
                                    onChange={this.handleSearchChange}
                                    autoFocus
                                />
                            </div>
                        )}
                        <ul className="fair-dropdown__options">
                            {filteredOptions.map((option) => {
                                const selected = this.isOptionSelected(
                                    option.value
                                );

                                return (
                                    <li
                                        key={option.value}
                                        className={`fair-dropdown__option ${
                                            selected
                                                ? 'fair-dropdown__option--selected'
                                                : ''
                                        }`}
                                        onClick={() =>
                                            this.handleOptionClick(option)
                                        }
                                    >
                                        {multi && (
                                            <span
                                                className={`fair-dropdown__checkbox ${
                                                    selected
                                                        ? 'fair-dropdown__checkbox--checked'
                                                        : ''
                                                }`}
                                            />
                                        )}
                                        <span className="fair-dropdown__option-label">
                                            {option.label}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    }
}

FairDropdown.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
                .isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
        })
    ).isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array,
    ]),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    allSelectedLabel: PropTypes.string,
    multi: PropTypes.bool,
    searchable: PropTypes.bool,
    clearable: PropTypes.bool,
    clearValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
};

FairDropdown.defaultProps = {
    multi: false,
    searchable: false,
    clearable: true,
    placeholder: '',
    allSelectedLabel: '',
    className: '',
};

export default FairDropdown;
